import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { CREATION_STAGES } from './creationAttendance';

/**
 * "Where is this patient up to?" — three buttons replacing the "Create at stage"
 * dropdown. One question, written in the practice's words; the answer decides
 * which table the patient lands in and what the report counts.
 */
export default function CreationStagePicker({ value, onChange, disabled = false }) {
    return (
        <Box sx={{ mb: 2.5 }} data-testid="creation-stage-picker">
            <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, mb: 0.5 }}>
                Where is this patient up to?
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#637381', mb: 1.25, textTransform: 'none' }}>
                Choose the one that is true today. This decides which table the patient appears in and what the report counts.
            </Typography>
            <Box role="radiogroup" aria-label="Where is this patient up to?" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
                {CREATION_STAGES.map((stage) => {
                    const selected = value === stage.value;
                    return (
                        <Box
                            key={stage.value}
                            component="button"
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            disabled={disabled}
                            onClick={() => { if (!disabled && !selected) onChange(stage.value); }}
                            sx={{
                                textAlign: 'left', cursor: disabled ? 'default' : 'pointer', background: '#fff',
                                borderRadius: '12px', p: '12px 14px', transition: 'all 150ms ease', font: 'inherit',
                                border: '1px solid', borderColor: selected ? '#00AB55' : 'rgba(145,158,171,0.24)',
                                boxShadow: selected ? '0 0 0 3px rgba(0,171,85,0.12)' : 'none',
                                '&:hover': disabled ? {} : { borderColor: selected ? '#00AB55' : '#919EAB' },
                                '&:focus-visible': { outline: '2px solid #00AB55', outlineOffset: '2px' },
                            }}
                        >
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#212B36', textTransform: 'none' }}>{stage.title}</Typography>
                            <Typography sx={{ fontSize: '12px', color: '#637381', mt: 0.5, lineHeight: 1.45, textTransform: 'none' }}>{stage.hint}</Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

CreationStagePicker.propTypes = {
    value: PropTypes.oneOf(['NEW', 'CONSULTATION_ATTENDED', 'TREATMENT_STARTED']).isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

CreationStagePicker.defaultProps = {
    disabled: false,
};
